/**
 * UC-42: GenerateAiNews
 */
import type { CityStats, NewsItem } from '../../../types';
import type { IAppLogger } from '../../domain/ports/IAppLogger';
import type { IAiAdvisor } from '../../domain/ports/IAiAdvisor';

export interface GenerateAiNewsInput {
  stats: CityStats;
  aiEnabled: boolean;
  /** Gate roll 0–1; news only generates when roll < threshold */
  gateRoll?: number;
  gateThreshold?: number;
  context?: string;
}

export interface GenerateAiNewsOutput {
  ok: boolean;
  news?: NewsItem;
  skipped?: boolean;
  reason?: string;
}

export interface GenerateAiNewsDependencies {
  aiAdvisor: IAiAdvisor;
  logger?: IAppLogger;
}

const DEFAULT_GATE_THRESHOLD = 0.05;

export class GenerateAiNewsUseCase {
  constructor(private readonly deps: GenerateAiNewsDependencies) {}

  async execute(input: GenerateAiNewsInput): Promise<GenerateAiNewsOutput> {
    if (!input.aiEnabled) {
      return { ok: false, reason: 'ai_disabled' };
    }

    const threshold = input.gateThreshold ?? DEFAULT_GATE_THRESHOLD;
    const gateRoll = input.gateRoll ?? Math.random();
    if (gateRoll > threshold) {
      return { ok: true, skipped: true, reason: 'probability_gate' };
    }

    try {
      const news = await this.deps.aiAdvisor.generateNews(input.stats, input.context);

      this.deps.logger?.log('debug', 'ai.news_generated', { newsId: news.id });

      return { ok: true, news };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.deps.logger?.log('warn', 'ai.news_failed', { error: message });
      return { ok: false, reason: message };
    }
  }
}